import React, { useState, useEffect, useCallback, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { FiCamera, FiX } from "react-icons/fi";

import { HeaderTitle } from "../../../../components/HeaderTitle";
import {
  Container,
  Form,
  ContentForm,
  PictureTask,
  PicturePreview,
  NoPicturePreview,
  ControlsUploadImage,
  RemovePicture,
  ContentButton,
} from "./styles";
import { Input } from "../../../../components/Input";
import { SelectInput } from "../../../../components/SelectInput";
import { Button } from "../../../../components/Button";
import { useNotification } from "../../../../hooks/useNotification";
import { useAuth } from "../../../../hooks/useAuth";
import { status } from "../../data";
import { IFormCreateTask } from "../../interface";
import { loadCollaborators } from "../../../../services/collaborators";
import {
  ICollaboratorResponseAxios,
  OptionSelect,
} from "../../../../common/interfaces";
import { createTask } from "../../../../services/tasks";
import { useTranslate } from "../../../../hooks/useTranslate";

import "leaflet/dist/leaflet.css";

const CreateTask = () => {
  const { errorNotification, successNotification } = useNotification();
  const navigate = useNavigate();
  const { translate } = useTranslate();

  const { propertiesIds, user, application } = useAuth();

  const [loading, setLoading] = useState(false);
  const [preImage, setPreImage] = useState<File | string>("");
  const [image, setImage] = useState<File | string>("");
  const [collaborators, setCollaborators] = useState<OptionSelect[]>([]);

  const schema = yup.object().shape({
    description: yup
      .string()
      .required(translate("global.description_required")),
    status: yup.object().shape({
      label: yup.string(),
      // .required(translate("tasks.status_required")),
      value: yup.string(),
      // .required(translate("tasks.status_required")),
    }),
    collaborator: yup.object().shape({
      label: yup.string().required(translate("tasks.collaborator_required")),
      value: yup.string().required(translate("tasks.collaborator_required")),
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IFormCreateTask>({
    resolver: yupResolver(schema),
  });

  // TODO: Criar componente para gerenciar foto
  const submitFile = (file: FileList | null) => {
    if (file == null) return;

    thePreviewImage(file[0]);
    setImage(file[0]);
  };

  const thePreviewImage = (file: File) => {
    setPreImage(URL.createObjectURL(file));
  };

  const submitForm = async (data: IFormCreateTask) => {
    // if (!image) return errorNotification(translate("tasks.image_required"));

    const { deadlineConclusion, status, description, collaborator } = data;

    try {
      setLoading(true);
      const form = new FormData();

      if (deadlineConclusion) form.append("dateEnd", deadlineConclusion);

      if (image) {
        form.append("photo", image);
      }

      form.append("description", description);

      // if (status.value) {
      form.append("status", "PENDING");
      // }

      form.append("idCollaborator", collaborator.value);

      form.append("idFarm", String(propertiesIds));

      const query = {
        application,
        creatorName: user.name,
      };
      await createTask({
        query,
        body: form,
      });
      setLoading(false);
      successNotification(translate("tasks.success_message"));
      navigate("/tasks");
    } catch (error) {
      errorNotification(translate("tasks.error_message"));
      setLoading(false);
    }
  };

  const boot = useCallback(async () => {
    try {
      const collaborators: ICollaboratorResponseAxios[] = [];
      const page = 1;
      const limit = 1000;
      const farmId = propertiesIds[0];

      const { data, meta } = await loadCollaborators({
        farmId,
        page,
        limit,
      });

      // collaborators.push(...data);

      // if (meta.pages > 1) {
      //   for (let index = page + 1; index <= meta.pages; index++) {
      //     const { data } = await loadCollaborators({
      //       farmId,
      //       page: index,
      //       limit,
      //     });
      //     collaborators.push(...data);
      //   }
      // }

      const collaboratorsFormatted = collaborators
        .filter((collaborator) => {
          const status = collaborator.attributes.find(
            (item) => item.type === "property"
          );

          return status?.value === "collaborator";
        })
        .map((collaborator) => ({
          label: collaborator.name,
          value: collaborator.id,
        }));

      setCollaborators([
        { label: user.name, value: user.id },
        ...collaboratorsFormatted,
      ]);
    } catch (err) {
      errorNotification(translate("tasks.error_load_collaborators"));
    }
  }, [errorNotification, propertiesIds, user.id, translate, user.name]);

  const ready = useRef(false);

  useEffect(() => {
    if (!ready.current) {
      ready.current = true;
      if (user.id) boot();
    }
  }, [boot, user]);

  return (
    <Container>
      <HeaderTitle title={translate("tasks.title_new")} url="/tasks" showIcon />

      <PictureTask>
        {preImage ? (
          <PicturePreview>
            <img src={String(preImage)} alt="Property" />
          </PicturePreview>
        ) : (
          <NoPicturePreview>
            <FiCamera />
            <p>{translate("tasks.add_image")}</p>
          </NoPicturePreview>
        )}
        <ControlsUploadImage>
          <div>
            <label htmlFor="upload-file">
              {translate("tasks.select_image")}
            </label>
            <input
              onChange={(e) => {
                submitFile(e.target.files);
                e.target.value = "";
              }}
              accept="image/*"
              id="upload-file"
              type="file"
            />
          </div>
          {preImage && (
            <RemovePicture>
              <FiX
                onClick={() => {
                  setPreImage("");
                  setImage("");
                }}
              />
            </RemovePicture>
          )}
        </ControlsUploadImage>
      </PictureTask>

      <Form onSubmit={handleSubmit(submitForm)}>
        <ContentForm>
          <Input
            {...register("description")}
            error={errors.description?.message}
            placeholder={translate("tasks.placeholder_description")}
            type="text"
          />
          {/* <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <SelectInput
                {...field}
                options={status}
                placeholder={translate("tasks.placeholder_status")}
                error={errors.status?.label?.message}
              />
            )}
          /> */}
          <Controller
            name="collaborator"
            control={control}
            render={({ field }) => (
              <SelectInput
                {...field}
                options={collaborators}
                placeholder={translate("tasks.placeholder_collaborator")}
                error={errors.collaborator?.label?.message}
              />
            )}
          />
          <Input
            {...register("deadlineConclusion")}
            error={errors.deadlineConclusion?.message}
            placeholder={translate("tasks.placeholder_date_finish")}
            type="date"
          />
        </ContentForm>

        <ContentButton>
          <Button
            isLoading={loading}
            type="submit"
            label={translate("tasks.label_btn_save")}
          />
        </ContentButton>
      </Form>
    </Container>
  );
};

export { CreateTask };
